import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Package, DollarSign, Target, Plus, Search, Edit2, Trash2, X, Save, RefreshCw, Calendar, Lock
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function ProductsPricing({ companyId: propCompanyId }) {
  const { colors } = useTheme();
  const { showConfirm, showSuccess, showError } = useModal();
  const [activeTab, setActiveTab] = useState('products'); // products, pricing, plans, sources
  const [loading, setLoading] = useState(true);
  const [skuCodeVisible, setSkuCodeVisible] = useState(false); // Default hidden for simplicity
  const [searchTerm, setSearchTerm] = useState('');
  const [companyData, setCompanyData] = useState(null); // For checking recurring_billing_enabled

  // Products (SKUs)
  const [skus, setSkus] = useState([]);
  const [filteredSkus, setFilteredSkus] = useState([]);
  const [showSkuModal, setShowSkuModal] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [skuForm, setSkuForm] = useState({
    code: '',
    name: '',
    description: '',
    base_cost: '',
    category: '',
    billing_type: 'one_time',
    recurring_interval: ''
  });

  // Service Plans (for recurring businesses)
  const [servicePlans, setServicePlans] = useState([]);
  const [filteredServicePlans, setFilteredServicePlans] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    billing_interval: 'monthly',
    price: '',
    setup_fee: '',
    features: '',
    is_active: true
  });
  
  // Pricing Tables
  const [pricingTables, setPricingTables] = useState([]);
  const [filteredPricingTables, setFilteredPricingTables] = useState([]);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    name: '',
    description: '',
    markup_percentage: '',
    is_active: true
  });
  
  // Lead Sources
  const [leadSources, setLeadSources] = useState([]);
  const [filteredLeadSources, setFilteredLeadSources] = useState([]);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceForm, setSourceForm] = useState({
    name: '',
    commissionable: false,
    commission_rate: 0
  });
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    // Use propCompanyId if provided, otherwise fall back to fetching it
    if (propCompanyId) {
      setCompanyId(propCompanyId);
      loadCompanySettings(propCompanyId);
    } else {
      loadCompanyAndData();
    }
  }, [propCompanyId]);

  useEffect(() => {
    filterData();
  }, [activeTab, searchTerm, skus, pricingTables, leadSources, servicePlans]);

  const loadCompanySettings = async (compId) => {
    try {
      // Load company settings for SKU visibility and recurring billing
      const { data: compData } = await supabaseData
        .from('companies')
        .select('sku_code_visible, recurring_billing_enabled')
        .eq('id', compId)
        .single();

      setCompanyData(compData);
      setSkuCodeVisible(compData?.sku_code_visible ?? false);
      loadData(compId);
    } catch (error) {
      console.warn('ProductsPricing: loadCompanySettings error', error);
      setLoading(false);
    }
  };

  const loadCompanyAndData = async () => {
    try {
      // Get company ID from user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: userCompany } = await supabaseData
        .from('user_companies')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (userCompany) {
        setCompanyId(userCompany.company_id);
        loadCompanySettings(userCompany.company_id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.warn('ProductsPricing: loadCompanyAndData error', error);
      setLoading(false);
    }
  };

  const loadData = async (compId) => {
    // Safety timeout - ensure loading state is cleared after 10 seconds max
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 10000);
    
    try {
      setLoading(true);

      // Load SKUs
      const { data: skusData, error: skusError } = await supabaseData
        .from('skus')
        .select('*')
        .order('code', { ascending: true });
      
      if (skusError) throw skusError;

      // Load Pricing Tables
      const { data: pricingData, error: pricingError } = await supabaseData
        .from('pricing_tables')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (pricingError) throw pricingError;

      // Load Lead Sources
      const { data: sourcesData, error: sourcesError } = await supabaseData
        .from('lead_sources')
        .select('*')
        .eq('company_id', compId)
        .order('name', { ascending: true });
      
      if (sourcesError) throw sourcesError;

      // Load Service Plans (for recurring businesses)
      const { data: plansData, error: plansError } = await supabaseData
        .from('service_plans')
        .select('*')
        .eq('company_id', compId)
        .order('sort_order', { ascending: true });
      
      if (plansError) throw plansError;

      setSkus(skusData || []);
      setPricingTables(pricingData || []);
      setLeadSources(sourcesData || []);
      setServicePlans(plansData || []);
    } catch (error) {
      console.warn('ProductsPricing: loadData error', error);
      // On auth error, loading will be cleared by timeout
    } finally {
      clearTimeout(loadingTimeout);
      setLoading(false);
    }
  };

  const filterData = () => {
    if (!searchTerm) {
      setFilteredSkus(skus);
      setFilteredPricingTables(pricingTables);
      setFilteredLeadSources(leadSources);
      setFilteredServicePlans(servicePlans);
      return;
    }

    const term = searchTerm.toLowerCase();

    if (activeTab === 'products') {
      setFilteredSkus(skus.filter(sku =>
        sku.code?.toLowerCase().includes(term) ||
        sku.name?.toLowerCase().includes(term) ||
        sku.category?.toLowerCase().includes(term)
      ));
    } else if (activeTab === 'pricing') {
      setFilteredPricingTables(pricingTables.filter(table =>
        table.name?.toLowerCase().includes(term) ||
        table.description?.toLowerCase().includes(term)
      ));
    } else if (activeTab === 'plans') {
      setFilteredServicePlans(servicePlans.filter(plan =>
        plan.name?.toLowerCase().includes(term) ||
        plan.description?.toLowerCase().includes(term)
      ));
    } else if (activeTab === 'sources') {
      setFilteredLeadSources(leadSources.filter(source =>
        source.name?.toLowerCase().includes(term)
      ));
    }
  };

  // Auto-generate SKU code from name (e.g., "Spring Replacement" -> "SPRING-REP-001")
  const generateSkuCode = (name) => {
    if (!name) return '';
    // Take first 2 words, uppercase, max 4 chars each
    const words = name.trim().toUpperCase().split(/\s+/).slice(0, 2);
    const prefix = words.map(w => w.replace(/[^A-Z0-9]/g, '').slice(0, 4)).join('-');
    // Add random suffix to ensure uniqueness
    const suffix = Math.floor(Math.random() * 900 + 100).toString();
    return `${prefix}-${suffix}`;
  };

  // SKU Functions
  const handleSaveSku = async (e) => {
    e.preventDefault();

    try {
      // Auto-generate code if empty or if SKU field is hidden
      let code = skuForm.code?.trim();
      if (!code || !skuCodeVisible) {
        code = generateSkuCode(skuForm.name);
      }

      const data = {
        code: code,
        name: skuForm.name,
        description: skuForm.description,
        base_cost: parseFloat(skuForm.base_cost) || 0,
        category: skuForm.category,
        billing_type: skuForm.billing_type || 'one_time',
        recurring_interval: skuForm.billing_type === 'recurring' ? skuForm.recurring_interval : null,
        company_id: companyId
      };

      if (editingSku) {
        const { error } = await supabaseData
          .from('skus')
          .update(data)
          .eq('id', editingSku.id);
        if (error) throw error;
      } else {
        // Check for duplicate product name
        const existing = skus.find(s => s.name.toLowerCase().trim() === skuForm.name.toLowerCase().trim());
        if (existing) {
          showError(`A product named "${existing.name}" already exists.`);
          return;
        }
        const { error } = await supabaseData
          .from('skus')
          .insert([data]);
        if (error) throw error;
        // Notify guided tour that a product was created
        window.dispatchEvent(new CustomEvent('tour:product-created'));
      }

      setShowSkuModal(false);
      setEditingSku(null);
      resetSkuForm();
      loadData(companyId);
    } catch (error) {
      showError('Error saving product: ' + error.message);
    }
  };

  const handleDeleteSku = async (id) => {
    const confirmed = await showConfirm('Delete this product?', { title: 'Delete Product', confirmText: 'Delete', confirmStyle: 'danger' });
    if (!confirmed) return;
    
    try {
      const { error } = await supabaseData
        .from('skus')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      loadData();
    } catch (error) {
      showError('Error deleting: ' + error.message);
    }
  };

  const resetSkuForm = () => {
    setSkuForm({
      code: '',
      name: '',
      description: '',
      base_cost: '',
      category: '',
      billing_type: 'one_time',
      recurring_interval: ''
    });
  };

  // Service Plan Functions
  const handleSavePlan = async (e) => {
    e.preventDefault();

    try {
      // Validation
      const trimmedName = (planForm.name || '').trim();
      if (!trimmedName) {
        showError('Service plan name is required');
        return;
      }

      // Check for duplicates (case-insensitive, excluding the current record when editing)
      const dup = servicePlans.find(p =>
        p.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        p.id !== editingPlan?.id
      );
      if (dup) {
        showError(`A service plan named "${trimmedName}" already exists`);
        return;
      }

      // Parse features from comma-separated string to array
      const featuresArray = planForm.features
        ? planForm.features.split(',').map(f => f.trim()).filter(f => f)
        : [];

      const data = {
        company_id: companyId,
        name: trimmedName,
        description: planForm.description,
        billing_interval: planForm.billing_interval,
        price: parseFloat(planForm.price) || 0,
        setup_fee: parseFloat(planForm.setup_fee) || 0,
        features: featuresArray,
        is_active: planForm.is_active,
        sort_order: editingPlan?.sort_order || servicePlans.length
      };

      if (editingPlan) {
        const { error } = await supabaseData
          .from('service_plans')
          .update(data)
          .eq('id', editingPlan.id);
        if (error) throw error;
        showSuccess('Service plan updated!');
      } else {
        const { error } = await supabaseData
          .from('service_plans')
          .insert([data]);
        if (error) throw error;
        showSuccess('Service plan created!');
      }

      setShowPlanModal(false);
      setEditingPlan(null);
      resetPlanForm();
      loadData(companyId);
    } catch (error) {
      showError('Error saving service plan: ' + error.message);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description || '',
      billing_interval: plan.billing_interval,
      price: plan.price?.toString() || '',
      setup_fee: plan.setup_fee?.toString() || '',
      features: plan.features?.join(', ') || '',
      is_active: plan.is_active
    });
    setShowPlanModal(true);
  };

  const handleDeletePlan = async (id) => {
    const confirmed = await showConfirm('Delete this service plan? Existing subscriptions will be affected.', {
      title: 'Delete Service Plan',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('service_plans')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showSuccess('Service plan deleted!');
      loadData(companyId);
    } catch (error) {
      showError('Error deleting: ' + error.message);
    }
  };

  const handleTogglePlanActive = async (plan) => {
    try {
      const { error } = await supabaseData
        .from('service_plans')
        .update({ is_active: !plan.is_active })
        .eq('id', plan.id);

      if (error) throw error;
      loadData(companyId);
    } catch (error) {
      showError('Error toggling plan: ' + error.message);
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      billing_interval: 'monthly',
      price: '',
      setup_fee: '',
      features: '',
      is_active: true
    });
  };

  const getBillingIntervalLabel = (interval) => {
    const labels = {
      weekly: '/week',
      monthly: '/month',
      quarterly: '/quarter',
      annual: '/year'
    };
    return labels[interval] || '';
  };

  // Pricing Functions
  const handleSavePricing = async (e) => {
    e.preventDefault();

    try {
      const data = {
        name: pricingForm.name,
        description: pricingForm.description,
        markup_percentage: parseFloat(pricingForm.markup_percentage) || 0,
        is_active: pricingForm.is_active,
        company_id: companyId
      };

      if (editingPricing) {
        const { error } = await supabaseData
          .from('pricing_tables')
          .update(data)
          .eq('id', editingPricing.id);
        if (error) throw error;
      } else {
        const { error } = await supabaseData
          .from('pricing_tables')
          .insert([data]);
        if (error) throw error;
      }
      
      setShowPricingModal(false);
      setEditingPricing(null);
      resetPricingForm();
      loadData();
    } catch (error) {
      showError('Error saving pricing table: ' + error.message);
    }
  };

  const handleDeletePricing = async (id) => {
    const confirmed = await showConfirm('Delete this pricing table?', { title: 'Delete Pricing Table', confirmText: 'Delete', confirmStyle: 'danger' });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('pricing_tables')
        .delete()
        .eq('id', id);

      if (error) throw error;
      loadData();
    } catch (error) {
      showError('Error deleting: ' + error.message);
    }
  };

  const resetPricingForm = () => {
    setPricingForm({
      name: '',
      description: '',
      markup_percentage: '',
      is_active: true
    });
  };

  // Lead Source Functions
  const handleSaveSource = async (e) => {
    e.preventDefault();

    try {
      const trimmedName = (sourceForm.name || '').trim();
      if (!trimmedName) {
        showError('Lead source name is required');
        return;
      }

      // Case-insensitive duplicate check, excluding the current record when editing
      const dup = leadSources.find(s =>
        s.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        s.id !== editingSource?.id
      );
      if (dup) {
        showError(`A lead source named "${trimmedName}" already exists`);
        return;
      }

      if (editingSource) {
        // Update existing
        const { error } = await supabaseData
          .from('lead_sources')
          .update({
            name: (sourceForm.name || '').trim(),
            commissionable: sourceForm.commissionable,
            commission_rate: sourceForm.commission_rate
          })
          .eq('id', editingSource.id);

        if (error) throw error;
        showSuccess('Lead source updated!');
      } else {
        // Create new
        const { error } = await supabaseData
          .from('lead_sources')
          .insert([{
            company_id: companyId,
            name: (sourceForm.name || '').trim(),
            commissionable: sourceForm.commissionable,
            commission_rate: sourceForm.commission_rate,
            active: true
          }]);

        if (error) throw error;
        showSuccess('Lead source created!');
      }

      setShowSourceModal(false);
      setEditingSource(null);
      setSourceForm({ name: '', commissionable: false, commission_rate: 0 });
      loadData(companyId);
    } catch (error) {
      showError('Error saving lead source: ' + error.message);
    }
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setSourceForm({
      name: source.name,
      commissionable: source.commissionable,
      commission_rate: source.commission_rate
    });
    setShowSourceModal(true);
  };

  const handleToggleSource = async (source) => {
    try {
      const { error } = await supabaseData
        .from('lead_sources')
        .update({ active: !source.active })
        .eq('id', source.id);

      if (error) throw error;
      loadData(companyId);
    } catch (error) {
      showError('Error toggling source: ' + error.message);
    }
  };

  const handleDeleteSource = async (id) => {
    const confirmed = await showConfirm('Delete this lead source? This cannot be undone.', { title: 'Delete Lead Source', confirmText: 'Delete', confirmStyle: 'danger' });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('lead_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showSuccess('Lead source deleted!');
      loadData(companyId);
    } catch (error) {
      showError('Error deleting source: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            💰 Products & Pricing
          </h1>
          <p style={{ margin: 0, color: colors.textMuted }}>
            Manage your catalog, pricing strategies, and lead sources
          </p>
        </div>
        <button
          data-tour-id="add-product-button"
          onClick={() => {
            if (activeTab === 'products') {
              setEditingSku(null);
              resetSkuForm();
              setShowSkuModal(true);
            } else if (activeTab === 'pricing') {
              setEditingPricing(null);
              resetPricingForm();
              setShowPricingModal(true);
            } else if (activeTab === 'plans') {
              setEditingPlan(null);
              resetPlanForm();
              setShowPlanModal(true);
            } else {
              setShowSourceModal(true);
            }
          }}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Plus size={20} />
          Add {activeTab === 'products' ? 'Product' : activeTab === 'pricing' ? 'Pricing Table' : activeTab === 'plans' ? 'Service Plan' : 'Source'}
        </button>
      </div>

      {/* Tabs + Search */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('products')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'products' ? colors.accent : colors.bgInput,
                color: activeTab === 'products' ? '#fff' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📦 Products ({skus.length})
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'pricing' ? colors.accent : colors.bgInput,
                color: activeTab === 'pricing' ? '#fff' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              💰 Pricing ({pricingTables.length})
            </button>
            {/* Service Plans tab - only for recurring businesses */}
            {companyData?.recurring_billing_enabled && (
              <button
                onClick={() => setActiveTab('plans')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: activeTab === 'plans' ? colors.accent : colors.bgInput,
                  color: activeTab === 'plans' ? '#fff' : colors.textMuted,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                📋 Service Plans ({servicePlans.length})
              </button>
            )}
            <button
              onClick={() => setActiveTab('sources')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'sources' ? colors.accent : colors.bgInput,
                color: activeTab === 'sources' ? '#fff' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🎯 Lead Sources ({leadSources.length})
            </button>
          </div>

          {/* Search */}
          {activeTab !== 'sources' && (
            <div style={{ position: 'relative', minWidth: '250px' }}>
              <Search
                size={20}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.textMuted
                }}
              />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 3rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '0.95rem'
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'products' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {filteredSkus.map(sku => (
            <div
              key={sku.id}
              style={{
                background: colors.bgCard,
                borderRadius: '12px',
                padding: '1.5rem',
                border: `1px solid ${colors.border}`
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <div style={{
                  padding: '0.35rem 0.75rem',
                  background: colors.bgInput,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: colors.textMuted
                }}>
                  {sku.code}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setEditingSku(sku);
                      setSkuForm({
                        code: sku.code,
                        name: sku.name,
                        description: sku.description || '',
                        base_cost: sku.base_cost?.toString() || '',
                        category: sku.category || '',
                        billing_type: sku.billing_type || 'one_time',
                        recurring_interval: sku.recurring_interval || ''
                      });
                      setShowSkuModal(true);
                    }}
                    style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.info,
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteSku(sku.id)}
                    style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.error,
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <h3 style={{
                fontSize: '1.1rem',
                margin: '0 0 0.5rem 0',
                color: colors.text
              }}>
                {sku.name}
              </h3>
              {sku.description && (
                <p style={{
                  margin: '0 0 1rem 0',
                  color: colors.textMuted,
                  fontSize: '0.85rem'
                }}>
                  {sku.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '1rem',
                borderTop: `1px solid ${colors.border}`
              }}>
                <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Cost</span>
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: colors.success
                }}>
                  ${sku.base_cost?.toFixed(2) || '0.00'}
                </span>
              </div>
              {/* Billing Type & Category badges */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginTop: '0.75rem'
              }}>
                                {sku.category && (
                  <div style={{
                    padding: '0.35rem 0.75rem',
                    background: colors.bgInput,
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    color: colors.textMuted
                  }}>
                    {sku.category}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'pricing' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {filteredPricingTables.map(table => (
            <div
              key={table.id}
              style={{
                background: colors.bgCard,
                borderRadius: '12px',
                padding: '1.5rem',
                border: `1px solid ${colors.border}`
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <div style={{
                  padding: '0.35rem 0.75rem',
                  background: table.is_active ? `${colors.success}20` : `${colors.textMuted}20`,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: table.is_active ? colors.success : colors.textMuted
                }}>
                  {table.is_active ? 'Active' : 'Inactive'}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setEditingPricing(table);
                      setPricingForm({
                        name: table.name,
                        description: table.description || '',
                        markup_percentage: table.markup_percentage?.toString() || '',
                        is_active: table.is_active
                      });
                      setShowPricingModal(true);
                    }}
                    style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.info,
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeletePricing(table.id)}
                    style={{
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.error,
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                margin: '0 0 0.5rem 0',
                color: colors.text
              }}>
                {table.name}
              </h3>
              {table.description && (
                <p style={{
                  margin: '0 0 1rem 0',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  {table.description}
                </p>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '1rem',
                borderTop: `1px solid ${colors.border}`
              }}>
                <span style={{ color: colors.textMuted, fontSize: '0.95rem' }}>Markup</span>
                <span style={{
                  fontSize: '1.75rem',
                  fontWeight: 'bold',
                  color: colors.warning
                }}>
                  {table.markup_percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Service Plans Tab Content */}
      {activeTab === 'plans' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {filteredServicePlans.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '3rem',
              color: colors.textMuted
            }}>
              <Calendar size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>No service plans yet. Create your first plan to offer recurring services to customers.</p>
            </div>
          ) : (
            filteredServicePlans.map(plan => (
              <div
                key={plan.id}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: `1px solid ${colors.border}`,
                  opacity: plan.is_active ? 1 : 0.6
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    padding: '0.35rem 0.75rem',
                    background: plan.is_active ? `${colors.success}20` : `${colors.textMuted}20`,
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: plan.is_active ? colors.success : colors.textMuted
                  }}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleTogglePlanActive(plan)}
                      style={{
                        padding: '0.5rem',
                        background: colors.bgInput,
                        border: 'none',
                        borderRadius: '6px',
                        color: plan.is_active ? colors.warning : colors.success,
                        cursor: 'pointer'
                      }}
                      title={plan.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <RefreshCw size={14} />
                    </button>
                    <button
                      onClick={() => handleEditPlan(plan)}
                      style={{
                        padding: '0.5rem',
                        background: colors.bgInput,
                        border: 'none',
                        borderRadius: '6px',
                        color: colors.info,
                        cursor: 'pointer'
                      }}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan.id)}
                      style={{
                        padding: '0.5rem',
                        background: colors.bgInput,
                        border: 'none',
                        borderRadius: '6px',
                        color: colors.error,
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <h3 style={{
                  fontSize: '1.25rem',
                  margin: '0 0 0.5rem 0',
                  color: colors.text
                }}>
                  {plan.name}
                </h3>

                {plan.description && (
                  <p style={{
                    margin: '0 0 1rem 0',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>
                    {plan.description}
                  </p>
                )}

                {/* Price Display */}
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.25rem',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: colors.accent
                  }}>
                    ${parseFloat(plan.price).toFixed(2)}
                  </span>
                  <span style={{ color: colors.textMuted }}>
                    {getBillingIntervalLabel(plan.billing_interval)}
                  </span>
                </div>

                {plan.setup_fee > 0 && (
                  <div style={{
                    fontSize: '0.85rem',
                    color: colors.textMuted,
                    marginBottom: '1rem'
                  }}>
                    + ${parseFloat(plan.setup_fee).toFixed(2)} setup fee
                  </div>
                )}

                {/* Features */}
                {plan.features && plan.features.length > 0 && (
                  <div style={{
                    borderTop: `1px solid ${colors.border}`,
                    paddingTop: '1rem',
                    marginTop: '0.5rem'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: colors.textMuted,
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      fontWeight: 'bold'
                    }}>
                      Includes
                    </div>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '1.25rem',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}>
                      {plan.features.map((feature, idx) => (
                        <li key={idx} style={{ marginBottom: '0.25rem' }}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'sources' && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredLeadSources.map(source => (
              <div
                key={source.id}
                style={{
                  background: colors.bgInput,
                  padding: '1.25rem',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}
              >
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: source.active ? colors.text : '#666',
                    marginBottom: '0.5rem'
                  }}>
                    {source.name}
                  </div>
                  {source.commissionable && (
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: `${colors.accent}20`,
                      border: `1px solid ${colors.accent}`,
                      borderRadius: '6px',
                      color: colors.accent,
                      fontSize: '0.85rem',
                      fontWeight: 'bold'
                    }}>
                      {source.commission_rate}% Commission
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <button
                    onClick={() => handleEditSource(source)}
                    style={{
                      padding: '0.5rem',
                      background: colors.info,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleToggleSource(source)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: source.active ? colors.success : colors.textMuted,
                      color: source.active ? '#000' : '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {source.active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => handleDeleteSource(source.id)}
                    style={{
                      padding: '0.5rem',
                      background: colors.border,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.error,
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SKU Modal */}
      {showSkuModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingSku ? 'Edit Product' : 'Add Product'}
              </h3>
              <button
                onClick={() => {
                  setShowSkuModal(false);
                  setEditingSku(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveSku} style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {/* Only show SKU Code field if enabled in settings */}
                {skuCodeVisible && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      SKU Code
                    </label>
                    <input
                      type="text"
                      value={skuForm.code}
                      onChange={(e) => setSkuForm({ ...skuForm, code: e.target.value })}
                      placeholder="Leave blank to auto-generate"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={skuForm.name}
                    onChange={(e) => setSkuForm({ ...skuForm, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Description
                  </label>
                  <textarea
                    value={skuForm.description}
                    onChange={(e) => setSkuForm({ ...skuForm, description: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Base Cost *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={skuForm.base_cost}
                      onChange={(e) => setSkuForm({ ...skuForm, base_cost: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={skuForm.category}
                      onChange={(e) => setSkuForm({ ...skuForm, category: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                              </div>
              <button
                type="submit"
                style={{
                  width: '100%',
                  marginTop: '2rem',
                  padding: '1rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {editingSku ? 'Update Product' : 'Add Product'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricingModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingPricing ? 'Edit Pricing Table' : 'Add Pricing Table'}
              </h3>
              <button
                onClick={() => {
                  setShowPricingModal(false);
                  setEditingPricing(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSavePricing} style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Table Name *
                  </label>
                  <input
                    type="text"
                    value={pricingForm.name}
                    onChange={(e) => setPricingForm({ ...pricingForm, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Description
                  </label>
                  <textarea
                    value={pricingForm.description}
                    onChange={(e) => setPricingForm({ ...pricingForm, description: e.target.value })}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Markup Percentage *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={pricingForm.markup_percentage}
                    onChange={(e) => setPricingForm({ ...pricingForm, markup_percentage: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pricingForm.is_active}
                      onChange={(e) => setPricingForm({ ...pricingForm, is_active: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ color: colors.text }}>Active</span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                style={{
                  width: '100%',
                  marginTop: '2rem',
                  padding: '1rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {editingPricing ? 'Update Table' : 'Add Table'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Lead Source Modal */}
      {showSourceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingSource ? 'Edit Lead Source' : 'Add Lead Source'}
              </h3>
              <button
                onClick={() => {
                  setShowSourceModal(false);
                  setEditingSource(null);
                  setSourceForm({ name: '', commissionable: false, commission_rate: 0 });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveSource} style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.text,
                    fontWeight: 'bold'
                  }}>
                    Source Name *
                  </label>
                  <input
                    type="text"
                    value={sourceForm.name}
                    onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                    placeholder="e.g., Instagram, Yelp, Door Hanger"
                    required
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: colors.bgInput,
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={sourceForm.commissionable}
                      onChange={(e) => setSourceForm({ ...sourceForm, commissionable: e.target.checked })}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <div>
                      <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        Commissionable
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        Pay commission on leads from this source
                      </div>
                    </div>
                  </label>
                </div>

                {sourceForm.commissionable && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: 'bold'
                    }}>
                      Commission Rate % *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={sourceForm.commission_rate}
                      onChange={(e) => setSourceForm({ ...sourceForm, commission_rate: parseFloat(e.target.value) || 0 })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowSourceModal(false);
                    setEditingSource(null);
                    setSourceForm({ name: '', commissionable: false, commission_rate: 0 });
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!sourceForm.name.trim()}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: sourceForm.name.trim() ? colors.accent : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: sourceForm.name.trim() ? '#fff' : '#666',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: sourceForm.name.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  {editingSource ? 'Save Changes' : 'Add Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Plan Modal */}
      {showPlanModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            border: `1px solid ${colors.border}`,
            margin: 'auto'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingPlan ? 'Edit Service Plan' : 'Add Service Plan'}
              </h3>
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  setEditingPlan(null);
                  resetPlanForm();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSavePlan} style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={planForm.name}
                    onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                    placeholder="e.g., Basic Maintenance, Premium Care"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Description
                  </label>
                  <textarea
                    value={planForm.description}
                    onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                    placeholder="Brief description of what this plan includes"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={planForm.price}
                      onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Billing Interval *
                    </label>
                    <select
                      value={planForm.billing_interval}
                      onChange={(e) => setPlanForm({ ...planForm, billing_interval: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Setup Fee (optional)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planForm.setup_fee}
                    onChange={(e) => setPlanForm({ ...planForm, setup_fee: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Features (comma-separated)
                  </label>
                  <textarea
                    value={planForm.features}
                    onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                    placeholder="e.g., Monthly inspection, Priority scheduling, 10% discount on repairs"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                    Separate features with commas
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={planForm.is_active}
                      onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ color: colors.text }}>Active (available for new subscriptions)</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  marginTop: '2rem',
                  padding: '1rem',
                  background: colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
